import React, { useState, useEffect, useCallback } from 'react';
import Divider from '@material-ui/core/Divider';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import api from '~/services';
import * as lib from '~/dependencies';
import { openExternalURL, truncate, log } from '~/helpers';
import { IntegrationIds } from '~/helpers/contactsIntegration';
import {
  ContactIntegratedList,
  ContactIntegrated,
} from '~/interfaces/integrations';
import * as Loaders from '~/components/Loaders';

import ContactItemActions from './ListItemActions';
import Interaction from './Interaction';

import {
  List,
  ListItem,
  ContactDetails,
  ButtonOpenContactContainer,
  TextLabelContainer,
  LabelContent,
  HorizontalContactList,
  Navigations,
  InteractionsContainer,
} from './styles';

const { GO_HIGH_LEVEL_ID, ZENDESK_ID, ACTIVE_CAMPAIGN_ID } = IntegrationIds;

const denyOwnerList = [ZENDESK_ID, ACTIVE_CAMPAIGN_ID];

function TextLabel({ label, text }: { label: string; text: string | null }) {
  return (
    <TextLabelContainer>
      <LabelContent>{label}</LabelContent>
      {text}
    </TextLabelContainer>
  );
}

type Props = {
  contacts: ContactIntegratedList;
};

export default function ContactList(props: Props) {
  const [index, setIndex] = useState(0);
  const [interactions, setInteractions] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isConversationFav, setIsConversationFav] = useState(false);

  const [fetchingInteractions, setFetchingInteractions] = useState(true);

  const { contacts } = props;

  const [{ integrationInfo, integrationId, externalId }] = contacts;

  // NWA-2438 - Go High Level has sub-accounts
  const accountId = String(integrationId).includes('.')
    ? String(integrationId).split('.')[1]
    : integrationId;

  const contact = contacts[index];

  const starConversation = useCallback(async () => {
    try {
      if (!isConversationFav) {
        await api.integrations.put.starConversation(accountId, conversationId);
        setIsConversationFav(true);
      } else {
        await api.integrations.put.unmarkStarConversation(
          accountId,
          conversationId
        );
        setIsConversationFav(false);
      }
    } catch (err) {
      log.error('Failed to execute the starConversation function.', err);
    }
  }, [accountId, conversationId, isConversationFav]);

  useEffect(() => {
    async function getGoHighLevelMessages() {
      try {
        const response = await api.integrations.get.goHighLevelLastMessages(
          accountId,
          externalId.split('/')[1]
        );

        if (response?.data?.messages) {
          setConversationId(response.data.conversationId);
          setInteractions(response.data.messages);
          setIsConversationFav(response.data.starred);
        }
      } catch (err) {
        log.error('Fetch Go High Level messages ', err);
      } finally {
        setFetchingInteractions(false);
      }
    }

    if (String(integrationId).startsWith(GO_HIGH_LEVEL_ID.toString()))
      getGoHighLevelMessages();
  }, [
    integrationId,
    externalId,
    accountId,
    setInteractions,
    setFetchingInteractions,
  ]);

  function handleContactNameClick(externalUrl: string | null) {
    if (externalUrl) openExternalURL(externalUrl);
  }

  function gotoNext() {
    setIndex(prevIndex =>
      prevIndex === contacts.length - 1 ? 0 : prevIndex + 1
    );
  }

  function gotoPrevious() {
    setIndex(prevIndex =>
      prevIndex === contacts.length - 1 ? 0 : prevIndex + 1
    );
  }

  return (
    <div>
      {String(integrationId).startsWith(GO_HIGH_LEVEL_ID.toString()) ? (
        <HorizontalContactList>
          {contacts.length ? (
            <Navigations>
              <NavigateBeforeIcon
                onClick={gotoPrevious}
                data-test-id="navigate-to-next-contact-button"
              />
              {index + 1} / {contacts.length}
              <NavigateNextIcon
                onClick={gotoNext}
                data-test-id="navigate-to-prev-contact-button"
              />
            </Navigations>
          ) : null}

          <List
            component="nav"
            aria-label="horizontal list of contacts"
            data-test-id="contact-list-horizontal"
            key={integrationId}>
            <ListItem key={integrationId}>
              <ContactDetails>
                <ButtonOpenContactContainer
                  onClick={() => handleContactNameClick(contact.externalUrl)}
                  data-test-id="open-contact-external-portal">
                  <span
                    aria-hidden="true"
                    onClick={() => handleContactNameClick(contact.externalUrl)}>
                    Check{' '}
                    {truncate(
                      contact?.fullNameFormatted || contact?.firstName,
                      15
                    )}{' '}
                    on {contact.integrationInfo.name}
                  </span>
                </ButtonOpenContactContainer>

                <TextLabel
                  label="Owner:"
                  text={contact?.ownerName || 'Unassigned'}
                  data-test-id="contact-onwer"
                />
                <TextLabel
                  label="Contact Type:"
                  // Go High Level external id e.g:
                  text={contact?.externalId?.split('/')[0]}
                  data-test-id="contact-type"
                />

                <TextLabel
                  label="Company:"
                  text={contact?.companyName || 'Unassigned'}
                  data-test-id="company-name"
                />

                {!fetchingInteractions && (
                  <ContactItemActions contact={contact} />
                )}
              </ContactDetails>

              <InteractionsContainer>
                {interactions?.length ? (
                  <>
                    <div className="header">
                      <div className="header-title">
                        <span> Last {interactions?.length} Interactions</span>
                      </div>
                      <div
                        className="header-favorite"
                        onClick={starConversation}
                        aria-hidden="true"
                        title={
                          isConversationFav
                            ? 'Unmark as starred'
                            : 'Mark as starred'
                        }
                        data-test-id="star-conversation-button">
                        {isConversationFav ? <StarIcon /> : <StarBorderIcon />}
                      </div>
                    </div>

                    {interactions.map((interaction: any, interactionIndex) => (
                      <>
                        <Interaction
                          key={interaction.messageId}
                          data={interaction}
                        />
                        <Divider />
                        {interactionIndex < interactions.length - 1 && (
                          <Divider />
                        )}
                      </>
                    ))}
                  </>
                ) : null}

                {/* {!fetchingInteractions &&
                  interactions &&
                  interactions.length === 0 && (
                    <div className="header">No interactions available</div>
                  )} */}

                {fetchingInteractions ? (
                  <div className="header">
                    <Loaders.Line size="372" height={31} />
                  </div>
                ) : null}
              </InteractionsContainer>
            </ListItem>
          </List>
        </HorizontalContactList>
      ) : (
        <List component="nav" aria-label="list of contacts">
          {lib._.map(
            contacts,
            (contactInfo: ContactIntegrated, arrayIndex: number) => {
              return (
                <ListItem
                  button
                  key={`${contactInfo.externalId}-${arrayIndex}`}>
                  <ContactDetails>
                    <ButtonOpenContactContainer>
                      <span
                        aria-hidden="true"
                        onClick={() =>
                          handleContactNameClick(contactInfo.externalUrl)
                        }>
                        Check{' '}
                        {contactInfo?.fullNameFormatted ||
                          contactInfo?.firstName}{' '}
                        on {integrationInfo.name}
                      </span>
                    </ButtonOpenContactContainer>

                    {!denyOwnerList.includes(integrationId) && (
                      <TextLabel label="Owner:" text={contactInfo?.ownerName} />
                    )}

                    <TextLabel
                      label="Company:"
                      text={contactInfo?.companyName || 'Unassigned'}
                    />
                  </ContactDetails>

                  <ContactItemActions contact={contactInfo} />

                  {arrayIndex < contacts.length - 1 && <Divider absolute />}
                </ListItem>
              );
            }
          )}
        </List>
      )}
    </div>
  );
}
